import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { getFireflyAccountMapping, createFireflyAccountMapping, updateFireflyAccountMapping } from '../services/api';
import BackButton from '../components/BackButton';


const FireflyConfig = () => {
    const initialFormData = {
        revenue_film_nuovo_account_name: '',
        revenue_film_usato_account_name: '',
        revenue_musica_nuovo_account_name: '',
        rev_musica_usata_name: '',
        revenue_collezionabili_account_name: '',
        revenue_servizi_riversamenti_account_name: '',
        revenue_spese_spedizione_incasso_account_name: '',
        revenue_extra_account_name: '',
        expense_film_acquisto_account_name: '',
        expense_musica_acquisto_account_name: '',
        expense_materiali_servizi_riversamenti_account_name: '',
        expense_spese_imballaggio_account_name: '',
        expense_marketing_pubblicita_account_name: '',
        expense_spese_spedizioni_account_name: '',
        expense_commissioni_marketplace_account_name: '',
        expense_commissioni_bancarie_account_name: '',
        liability_iva_debito_account_name: '',
        inventory_products_account_name: '',
        inventory_services_account_name: '',
        cogs_products_account_name: '',
        cogs_services_account_name: '',
        cogs_marketplace_commissioni_account_name: '', // NEW
    };
    const [mapping, setMapping] = useState(null);
    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const fetchMapping = async () => {
            try {
                const response = await getFireflyAccountMapping();
                if (response && response.data) {
                    const fetchedData = response.data;
                    // Ensure all keys from the initial state are present, defaulting to '' if null/undefined
                    const completeData = Object.keys(initialFormData).reduce((acc, key) => {
                        acc[key] = fetchedData[key] || '';
                        return acc;
                    }, {});
                    setMapping(fetchedData);
                    setFormData(completeData);
                }
            } catch (err) {
                if (err.response && err.response.status === 404) {
                    // This is expected if no mapping exists yet.
                    console.log("Nessuna mappatura trovata, si procederà alla creazione.");
                } else {
                    setError('Errore nel recupero della configurazione Firefly III.');
                    console.error('Fetch error:', err);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchMapping();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        const dataToSend = { ...formData }; // Create a shallow copy

        try {
            if (mapping) {
                await updateFireflyAccountMapping(mapping.id, dataToSend);
            } else {
                await createFireflyAccountMapping(dataToSend);
            }
            setSuccess(true);
            
            // After successful save, refetch the mapping to update the UI
            const response = await getFireflyAccountMapping();
            if (response && response.data) {
                const fetchedData = response.data;
                // It's crucial to use the current formData keys to build completeData
                // This ensures new fields are included even if not explicitly defined in fetchedData
                const completeData = Object.keys(formData).reduce((acc, key) => {
                    acc[key] = fetchedData[key] || '';
                    return acc;
                }, {});
                setMapping(fetchedData);
                setFormData(completeData);
            }

            setTimeout(() => setSuccess(false), 3000); // Hide after 3 seconds
        } catch (err) {
            setError('Errore durante il salvataggio della configurazione.');
            console.error('Submit error:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <p>Caricamento configurazione...</p>;
    if (error) return <Alert variant="danger">{error}</Alert>;

    return (
        <Container className="d-flex flex-column" style={{ height: '100vh', maxWidth: '99%' }}>
            <Row className="align-items-center mb-3">
                <Col xs={12} md={6}>
                    <h1 className="text-danger mb-1">Conti automatici</h1>
                    <div className="text-muted">
                        Conti di Firefly azienda usati dalle registrazioni automatiche di ordini, fatture passive e Cassa IBS.
                        Non rinominarli in Firefly senza aggiornarli qui.
                    </div>
                </Col>
                <Col xs={12} md={6} className="text-md-end">
                    <BackButton />
                    <Button variant="success" className="ms-2" onClick={handleSubmit} disabled={loading}>
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        {mapping ? 'Salva Modifiche' : 'Crea Configurazione'}
                    </Button>
                </Col>
            </Row>

            <Form onSubmit={handleSubmit} className="flex-grow-1 overflow-auto">
                <Card className="mb-4 shadow-sm">
                    <Card.Header as="h5" className="bg-success text-white">Conti Ricavo</Card.Header>
                    <Card.Body>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Ricavo Film Nuovo (Es. MC - Vendita Film (Nuovo))</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="revenue_film_nuovo_account_name" value={formData.revenue_film_nuovo_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Ricavo Film Usato (Es. MC - Vendita Film (Usato))</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="revenue_film_usato_account_name" value={formData.revenue_film_usato_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Ricavo Musica Nuova (Es. MC - Vendita Musica (Nuovo))</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="revenue_musica_nuovo_account_name" value={formData.revenue_musica_nuovo_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Ricavo Musica Usata (Es. MC - Vendita Musica (Usata))</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="rev_musica_usata_name" value={formData.rev_musica_usata_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Ricavo Collezionabili (Es. MC - Vendita Collezionabili)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="revenue_collezionabili_account_name" value={formData.revenue_collezionabili_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Ricavo Servizi/Riversamenti (Es. MC - Servizi e Riversamenti)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="revenue_servizi_riversamenti_account_name" value={formData.revenue_servizi_riversamenti_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Ricavo Spese Spedizione Incasso (Es. MC - Spese di Spedizione)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="revenue_spese_spedizione_incasso_account_name" value={formData.revenue_spese_spedizione_incasso_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Ricavo Extra (Es. MC - Extra)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="revenue_extra_account_name" value={formData.revenue_extra_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                    </Card.Body>
                </Card>

                <Card className="mb-4 shadow-sm">
                    <Card.Header as="h5" className="bg-danger text-white">Conti Spesa</Card.Header>
                    <Card.Body>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Spesa Acquisto Film (Es. MC - Acquisto Film)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="expense_film_acquisto_account_name" value={formData.expense_film_acquisto_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Spesa Acquisto Musica (Es. MC - Acquisto Musica)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="expense_musica_acquisto_account_name" value={formData.expense_musica_acquisto_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Spesa Materiali/Servizi/Riversamenti (Es. MC - Materiali Servizi e Riversamenti)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="expense_materiali_servizi_riversamenti_account_name" value={formData.expense_materiali_servizi_riversamenti_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Spese Imballaggio (Es. MC - Spese Imballaggio)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="expense_spese_imballaggio_account_name" value={formData.expense_spese_imballaggio_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Spese Marketing/Pubblicità (Es. MC - Spese Marketing e Pubblicità)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="expense_marketing_pubblicita_account_name" value={formData.expense_marketing_pubblicita_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Spese Spedizioni (Costo Sostenuto) (Es. MC - Spese Spedizioni)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="expense_spese_spedizioni_account_name" value={formData.expense_spese_spedizioni_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Commissioni Marketplace (Es. MC - Commissioni Marketplace)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="expense_commissioni_marketplace_account_name" value={formData.expense_commissioni_marketplace_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Commissioni Bancarie (Es. MC - Commissioni Bancarie)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="expense_commissioni_bancarie_account_name" value={formData.expense_commissioni_bancarie_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                    </Card.Body>
                </Card>

                <Card className="mb-4 shadow-sm">
                    <Card.Header as="h5" className="bg-info text-white">Conti Passività</Card.Header>
                    <Card.Body>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Passività IVA a Debito (Es. MC - Passività - IVA a Debito)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="liability_iva_debito_account_name" value={formData.liability_iva_debito_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                    </Card.Body>
                </Card>

                <Card className="mb-4 shadow-sm">
                    <Card.Header as="h5" className="bg-warning">Conti Magazzino e COGS</Card.Header>
                    <Card.Body>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Conto Magazzino Prodotti (Es. MC - VALORE MAGAZZINO PRODOTTI)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="inventory_products_account_name" value={formData.inventory_products_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Conto Magazzino Servizi (Es. MC - VALORE MAGAZZINO SERVIZI)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="inventory_services_account_name" value={formData.inventory_services_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Conto Costo del Venduto (Prodotti) (Es. MC - Venduto Prodotti)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="cogs_products_account_name" value={formData.cogs_products_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Conto Costo del Venduto (Servizi) (Es. MC - Venduto Servizi)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="cogs_services_account_name" value={formData.cogs_services_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm="4">Costo del Venduto Commissioni Marketplace (Es. MC - Venduto Commissioni MKT)</Form.Label>
                            <Col sm="8">
                                <Form.Control type="text" name="cogs_marketplace_commissioni_account_name" value={formData.cogs_marketplace_commissioni_account_name} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                    </Card.Body>
                </Card>

                {success && <Alert variant="success" className="fixed-top text-center">Configurazione salvata con successo!</Alert>}
                {error && <Alert variant="danger">{error}</Alert>}
            </Form>
        </Container>
    );
};

export default FireflyConfig;
